import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounced } from '@helpers/useDebounced';
import { useAuth } from '@hooks/useAuth';
import { useCurrentWorkspace } from '@hooks/workspace';
import { useOrganization } from '@components/OrganizationBoundary';

const WelcomeModal = React.lazy(() => import('./WelcomeModal'));
const WelcomeModalContributor = React.lazy(
  () => import('./WelcomeModalContributor')
);

export default function WelcomeModalContainer() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth(true, 'internal');
  const { currentOrganization } = useOrganization(false);
  const [currentWorkspace] = useCurrentWorkspace(false);
  const [isOpen, setIsOpen] = useState(false);
  const isNewUserRef = useRef(user.isNewUser);
  isNewUserRef.current = user.isNewUser;
  const hasWelcome = !!searchParams.get('welcome');
  const disableOnboarding =
    !!currentOrganization?.entity?.features.disableOnboarding;

  const visibleDebounced = useDebounced(isOpen, 300, false);
  const visible = isOpen || visibleDebounced;
  const isContributor = currentWorkspace?.role === 'workspace://contributor';

  useEffect(() => {
    if (disableOnboarding) return;
    if (hasWelcome || !!isNewUserRef.current) {
      setIsOpen(true);
    }
  }, [hasWelcome, setIsOpen, disableOnboarding]);

  const onClose = useCallback(
    (tourType?: string) => {
      searchParams.delete('welcome');
      if (tourType) searchParams.set('startTour', tourType);
      setSearchParams(searchParams);
      setIsOpen(false);
    },
    [searchParams, setIsOpen, setSearchParams]
  );

  if (!visible) return null;

  if (isContributor) {
    return (
      <Suspense fallback={null}>
        <WelcomeModalContributor open={isOpen} onCloseModal={onClose} />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={null}>
      <WelcomeModal open={isOpen} onCloseModal={onClose} />
    </Suspense>
  );
}
